import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ComposerPrimitive, MessagePrimitive, ThreadPrimitive } from '@assistant-ui/react';
import { AssistentViewViewContent, ViewContentCenter } from '../../layout/Main/styled';

export const ComposerWrapper = styled(Box)({
  display: 'flex',
  gap: '8px',
  padding: '12px 10px',
  alignItems: 'center'
});

export const ComposerInput = styled(ComposerPrimitive.Input)(({ theme }) => ({
  flex: 1,
  height: '36px',
  backgroundColor: '#ffffff',
  border: `1px solid ${theme.palette.neutrals.beige3}`,
  borderRadius: '29px',
  padding: '10px 12px 9px 20px',
  fontFamily: 'F Grotesk Book',
  fontSize: '14px',
  lineHeight: '1.2',
  letterSpacing: '-0.14px',
  color: theme.palette.neutrals.lead,
  outline: 'none',
  resize: 'none',
  overflow: 'hidden',
  '&::placeholder': {
    color: theme.palette.neutrals.beige4
  }
}));

export const ComposerSendButton = styled(Box)(({ theme }) => ({
  width: '36px',
  height: '36px',
  minWidth: '36px',
  borderRadius: '50%',
  backgroundColor: theme.palette.brand.mediumGreen,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  color: '#ffffff',
  '&:disabled': {
    opacity: 0.4,
    cursor: 'not-allowed'
  }
}));

export const TermsOfUseLink = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.neutrals.beige3}`,
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2)
}));

export const SchedulingCardWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  alignItems: 'flex-start'
}));

export const SchedulingButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  width: 'auto',
  maxWidth: '100%',
  minHeight: '50px',
  backgroundColor: theme.palette.brand.lightGreen,
  borderRadius: '25px',
  textDecoration: 'none',
  padding: '12px 24px',
  flexShrink: 0
})) as typeof Box;

export const SchedulingButtonLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.brand.darkGreen,
  lineHeight: 1.4
}));

export const SchedulingSubtitle = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: 1.4,
  color: theme.palette.neutrals.lead,
  textAlign: 'center'
}));

export const TypingIndicatorWrapper = styled(Box)({
  backgroundColor: '#E0E9DF',
  borderRadius: '16px 16px 16px 4px',
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: '12px',
  paddingBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px'
});

export const TypingDot = styled(Box, {
  shouldForwardProp: prop => prop !== 'delay'
})<{ delay: string }>(({ theme, delay }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.brand.mediumGreen,
  animation: 'typingBounce 1.2s infinite ease-in-out',
  animationDelay: delay,
  '@keyframes typingBounce': {
    '0%, 60%, 100%': { transform: 'translateY(0)' },
    '30%': { transform: 'translateY(-6px)' }
  }
}));

export const AssistantMessageSchedulingWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minWidth: 0
}));

export const AssistantDisplayTextBubble = styled(Box)(({ theme }) => ({
  alignSelf: 'flex-start',
  maxWidth: '85%',
  backgroundColor: theme.palette.neutrals.beige2,
  borderRadius: '16px 16px 16px 4px',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  [theme.breakpoints.up('md')]: {
    maxWidth: '70%'
  }
}));

export const AssistantDisplayText = styled(Typography)({
  fontSize: '1rem',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap'
});

export const AssistantMessageBox = styled(Box)(({ theme }) => ({
  maxWidth: 'calc(85% - 48px)',
  backgroundColor: theme.palette.neutrals.beige2,
  borderRadius: '16px 16px 16px 4px',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  fontSize: '1rem',
  [theme.breakpoints.up('md')]: {
    maxWidth: 'calc(70% - 48px)'
  }
}));

export const ChatContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 220px)',
  minHeight: 280,
  [theme.breakpoints.up('md')]: {
    height: 'calc(100vh - 330px)'
  },
  [theme.breakpoints.down('md')]: {
    height: 'auto',
    flex: 1,
    minHeight: 0
  }
}));

export const ThreadRoot = styled(ThreadPrimitive.Root)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden'
});

export const ThreadViewport = styled(ThreadPrimitive.Viewport)({
  flex: 1,
  overflowY: 'auto',
  padding: '16px 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  scrollbarWidth: 'thin',
  scrollbarColor: 'transparent transparent'
});

export const UserMessageRoot = styled(MessagePrimitive.Root)({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-start',
  marginBottom: '8px',
  gap: '8px'
});

export const UserMessageBubble = styled(Box)(({ theme }) => ({
  maxWidth: 'calc(85% - 48px)',
  backgroundColor: theme.palette.brand.darkGreen,
  color: theme.palette.neutrals.beige1,
  borderRadius: '16px 16px 4px 16px',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  fontSize: '1rem',
  [theme.breakpoints.up('md')]: {
    maxWidth: 'calc(70% - 48px)'
  }
}));

export const UserAvatarBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.brand.lightGreen,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
}));

export const AssistantMessageRoot = styled(MessagePrimitive.Root)({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '8px',
  gap: '8px'
});

export const AssistantAvatarBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.brand.mediumGreen,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
}));

export const TriagemViewContent = styled(AssistentViewViewContent)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    height: '100dvh',
    minHeight: 0,
    padding: 0
  }
}));

export const TriagemContentCenter = styled(ViewContentCenter)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
});

export const BackButtonWrapper = styled(Box)({
  marginTop: 'auto'
});
